import React from 'react';
import { cn } from '@/lib/utils';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface NavMethod {
  methodName: string;
  methodValue: string;
  helpFieldName?: string;
}

interface MethodNavGroupProps {
  methods: NavMethod[];
  activeMethodName: string;
  onMethodChange?: (methodValue: string) => void;
  /** Fires immediately on click, before the 350ms sheet-swap delay.
   *  ShiftTimelineComparison uses this to update activeMethodIndex for
   *  instant timeline content swap. Additive breakdowns don't need it. */
  onImmediateSelect?: (methodValue: string) => void;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export const MethodNavGroup: React.FC<MethodNavGroupProps> = ({
  methods,
  activeMethodName,
  onMethodChange,
  onImmediateSelect,
}) => {
  const handleSwitch = (method: NavMethod) => {
    if (method.methodName === activeMethodName) return;
    onImmediateSelect?.(method.methodValue);
    if (method.helpFieldName) {
      const _win = window as unknown as Record<string, unknown>;
      _win.__pendingHelpOpen = method.helpFieldName;
      if (onMethodChange) {
        // Switch method immediately for a snappy feel that matches the
        // config-form radio click path. The old HelpButton unmounts
        // (sheet destroyed), the new one auto-opens via __pendingHelpOpen.
        _win.__exitAlreadyPlayed = true;
        onMethodChange(method.methodValue);
      } else {
        // Pure navigation — direct content swap within the open sheet.
        const nav = _win.navigateHelp;
        if (typeof nav === 'function') {
          (nav as (fieldName: string) => void)(method.helpFieldName);
        }
      }
    } else {
      onMethodChange?.(method.methodValue);
    }
  };

  if (methods.length <= 1) return null;

  return (
    <div className="flex items-center justify-center gap-1 flex-wrap">
      {methods.map((method) => {
        const isActive = method.methodName === activeMethodName;
        return (
          <button
            key={method.methodName}
            type="button"
            aria-pressed={isActive}
            onClick={() => handleSwitch(method)}
            className={cn(
              'inline-flex items-center rounded-[0.25rem] border px-2 py-1 font-mono text-[11px] sm:text-[12px] font-semibold',
              'motion-safe:transition-[color,background-color,border-color,opacity] motion-safe:duration-200',
              isActive
                ? 'border-primary/20 bg-primary/10 text-primary cursor-default'
                : 'border-border/40 bg-transparent text-muted-foreground/40 hover:text-muted-foreground/60 hover:border-border/60 cursor-pointer',
            )}
          >
            {method.methodName}
          </button>
        );
      })}
    </div>
  );
};
